import {
  getLeadStageStyle,
  LEAD_STAGE_LABELS,
  LEAD_STAGE_LABELS_SHORT,
  type LeadStage,
} from '../../../features/leads/index.js';

/**
 * Badge visual do estado do lead. Visual baseado em hierarquia de 4
 * níveis (`getLeadStageStyle`):
 *
 *   - **milestone** (proposta aceita, contrato assinado, cliente ativo):
 *     accent dourado sólido, uppercase, ícone, sombra. Escala 1.5×
 *     em relação ao chip neutro pra puxar olho.
 *   - **active** (proposta enviada, etc): outline accent dourado.
 *   - **waiting** (aguardando lead): outline warning amarelo.
 *   - **neutral** (new, default): cinza atual.
 *   - **lost** (perdido): outline destructive.
 *
 * Antes (PR-M1-01c-iii.a) usávamos `Badge` do DS com variants soltos.
 * Reportado pelo Alexandre 2026-04-25: os marcos críticos do funil
 * ("Proposta aceita", "Contrato assinado") apareciam com chip cinza
 * minúsculo, sem celebrar a vitória. Helper unifica + escala.
 *
 * Modo `compact` (default em listas densas — tabela de leads): usa
 * label curto (`LEAD_STAGE_LABELS_SHORT`) preservando o peso visual
 * de milestone (uppercase+bold+ícone+border). Modo full (banner,
 * detail page) usa label completo. Decidido com Alexandre 2026-04-25
 * pra eliminar quebra de linha na tabela densa sem perder destaque
 * visual do contrato assinado.
 */
export interface LeadStageBadgeProps {
  stage: LeadStage;
  /** Quando true, usa label curto (ex: "ASSINADO" ao invés de
   * "CONTRATO ASSINADO"). aria-label sempre carrega o label completo
   * pra acessibilidade. */
  compact?: boolean;
}

export function LeadStageBadge({ stage, compact = false }: LeadStageBadgeProps) {
  const style = getLeadStageStyle(stage);
  const fullLabel = LEAD_STAGE_LABELS[stage];
  const visibleLabel = compact ? LEAD_STAGE_LABELS_SHORT[stage] : fullLabel;
  return (
    <span
      className={`${style.badgeClassName} whitespace-nowrap`}
      role="status"
      aria-label={`Estado do lead: ${fullLabel}`}
    >
      {style.icon ? (
        <span aria-hidden="true" className="leading-none">
          {style.icon}
        </span>
      ) : null}
      <span>{visibleLabel}</span>
    </span>
  );
}
